import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Box,
  Tooltip,
} from '@mui/material';
import { Search, Close, FileDownload } from '@mui/icons-material';
import { fetchRequests } from '../../../actions/requestsActions';
import { exportRequestsDataCSV } from '../../../actions/exportCSVActions';
import RequestsTable from '../../RequestsTable/RequestsTable';
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import { DEFAULT_PAGE, DEFAULT_PER_PAGE, PARSE_INT_RADIX } from '../../../constants/pagination';

const RequestsPage = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { requests, pagination } = useSelector(state => state.requests);

  // Initial data fetch
  useEffect(() => {
    dispatch(fetchRequests(1, DEFAULT_PER_PAGE));
  }, []); // Only run on mount

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && e.shiftKey) {
        e.preventDefault();
        if (!isSearchOpen) {
          handleToggleSearch();
        }
      }
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        handleToggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleToggleSearch = () => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) {
      setSearchText("");
    } else {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handleExport = () => {
    dispatch(exportRequestsDataCSV());
  };

  const handlePageChange = (event, newPage) => {
    dispatch(fetchRequests(newPage + 1, pagination.per_page));
  };

  const handleRowsPerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, PARSE_INT_RADIX);
    dispatch(fetchRequests(1, newPerPage));
  };

  return (
    <MainContentContainer>
      <AppBar position="sticky" sx={{ width: '100%' }}>
        <Toolbar>
          {isSearchOpen ? (
            <Box display="flex" alignItems="center" flexGrow={1}>
              <InputBase
                inputRef={searchInputRef}
                value={searchText}
                onChange={handleSearchChange}
                sx={{
                  flexGrow: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: "white",
                  borderRadius: 1,
                  '& input': {
                    color: 'text.primary',
                  }
                }}
                placeholder="Search..."
              />
              <IconButton onClick={handleToggleSearch} color="inherit">
                <Close />
              </IconButton>
            </Box>
          ) : (
            <>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Requests
              </Typography>
              <Tooltip title="Search (⌘F)">
                <IconButton onClick={handleToggleSearch} color="inherit">
                  <Search />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to CSV">
                <IconButton onClick={handleExport} color="inherit">
                  <FileDownload />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ width: '100%' }}>
        <RequestsTable
          data={requests}
          searchText={searchText}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </MainContentContainer>
  );
};

export default RequestsPage;